// mermaid-render.js — browser-side rendering for `<pre class="mermaid">`
// blocks emitted by src/pandoc/filters/mermaid.lua.
//
// The mermaid library itself is injected separately (dist/assets/mermaid/
// mermaid.min.js, which assigns globalThis.mermaid) and only on pages that
// actually contain a diagram — it is ~2.7 MB, far too heavy to carry on every
// page. Everything here degrades to a no-op when it is absent: the diagram
// stays visible as its own source text, which is still readable.

const PROCESSED = 'phMermaidDone';

function cssVar(name, fallback) {
    try {
        const value = getComputedStyle(document.body || document.documentElement)
            .getPropertyValue(name)
            .trim();
        return value || fallback;
    } catch (e) {
        return fallback;
    }
}

// Map the pantarhei palette onto mermaid's `base` theme so diagrams inherit
// the page's theme instead of shipping mermaid's stock blue. An explicit
// `theme=` on the block (or in front matter) opts out and uses that theme
// verbatim, which is what an author asking for `dark` or `forest` wants.
function mermaidConfig(namedTheme) {
    const base = { startOnLoad: false, securityLevel: 'strict' };
    if (namedTheme) {
        return Object.assign(base, { theme: namedTheme });
    }
    const ink = cssVar('--ph-ink', '#2b2242');
    const accent = cssVar('--ph-accent', '#6c63ff');
    return Object.assign(base, {
        theme: 'base',
        themeVariables: {
            background: cssVar('--ph-paper', '#ffffff'),
            primaryColor: cssVar('--ph-accent-soft', '#ece9ff'),
            primaryTextColor: ink,
            primaryBorderColor: accent,
            secondaryColor: cssVar('--ph-note-bg', '#f6f2ff'),
            tertiaryColor: cssVar('--ph-code-bg', '#f3f0ff'),
            lineColor: cssVar('--ph-muted', '#655f7a'),
            textColor: ink,
            mainBkg: cssVar('--ph-accent-soft', '#ece9ff'),
            nodeBorder: accent,
            clusterBkg: cssVar('--ph-note-bg', '#f6f2ff'),
            clusterBorder: cssVar('--ph-rule', '#d9d4ea'),
            fontFamily: cssVar('--ph-font-body', 'sans-serif'),
            fontSize: '15px',
        },
    });
}

// Mermaid sizes each diagram itself: width="100%" plus an inline max-width at
// the diagram's natural size. That already does the right thing — a small
// flowchart keeps its size, a wide one shrinks to the column — so the only
// reason to touch it is an explicit fig-width from the author. Overriding the
// inline max-width blindly is what blows a narrow diagram up to full width.
function applyFigWidth(svg, figWidth) {
    if (!figWidth) return;
    svg.style.maxWidth = /^[\d.]+$/.test(figWidth) ? `${figWidth}px` : figWidth;
    svg.style.height = 'auto';
}

export async function initMermaid() {
    if (typeof document === 'undefined') return;
    const blocks = Array.from(document.querySelectorAll('pre.mermaid'))
        .filter((el) => !el.dataset[PROCESSED]);
    if (blocks.length === 0) return;

    const mermaid = typeof window !== 'undefined' ? window.mermaid : null;
    if (!mermaid || typeof mermaid.render !== 'function') return;

    let seq = 0;
    for (const el of blocks) {
        el.dataset[PROCESSED] = '1';
        // Re-initialised per block: mermaid's config is global, so this is the
        // only way a single page can mix a `theme=dark` diagram with the
        // palette-derived default.
        mermaid.initialize(mermaidConfig(el.dataset.theme));
        // &nbsp; survives some HTML pipelines and mermaid does not treat it as
        // whitespace, so the parse fails on what looks like a valid diagram.
        const source = el.textContent.replace(/\u00a0/g, ' ');
        const container = document.createElement('div');
        container.className = 'ph-mermaid-svg';
        try {
            const { svg } = await mermaid.render(`ph-mermaid-${++seq}`, source);
            container.innerHTML = svg;
        } catch (err) {
            // A syntax error must not cost the reader the diagram's content:
            // leave the source in place and say what went wrong.
            el.classList.add('ph-mermaid-error');
            el.setAttribute('title', String((err && err.message) || err));
            continue;
        }
        const svgEl = container.querySelector('svg');
        if (!svgEl) continue;
        applyFigWidth(svgEl, el.dataset.figWidth);
        if (el.dataset.alt) {
            svgEl.setAttribute('role', 'img');
            svgEl.setAttribute('aria-label', el.dataset.alt);
        }
        // The id stays on the wrapper, never on the <svg>: mermaid scopes the
        // stylesheet it embeds in each diagram by that svg's id, so renaming it
        // silently strips the diagram of all its colours.
        if (el.id) container.id = el.id;
        el.replaceWith(container);
    }
}

export default initMermaid;
